const displayNames = new Intl.DisplayNames(["en"], { type: "region" });

/** Retired or reserved codes that share an English name with the current code. */
const OBSOLETE_TO_CURRENT: Record<string, string> = {
  AN: "CW",
  BU: "MM",
  CS: "RS",
  DD: "DE",
  DY: "BJ",
  FX: "FR",
  HV: "BF",
  NH: "VU",
  RH: "ZW",
  SU: "RU",
  TP: "TL",
  UK: "GB",
  VD: "VN",
  YD: "YE",
  YU: "RS",
  ZR: "CD",
};

/** Names stored on intel.contacts that do not match the current English region name. */
const EXTRA_ALIASES: Record<string, string> = {
  "united states of america": "US",
  usa: "US",
  "u.s.": "US",
  "u.s.a.": "US",
  uk: "GB",
  "great britain": "GB",
  england: "GB",
  "korea, republic of": "KR",
  "republic of korea": "KR",
  korea: "KR",
  turkiye: "TR",
  turkey: "TR",
  "cote d'ivoire": "CI",
  "ivory coast": "CI",
  "viet nam": "VN",
  "czech republic": "CZ",
  "hong kong": "HK",
  "trinidad and tobago": "TT",
  "el salvador": "SV",
  "russian federation": "RU",
  russia: "RU",
  uae: "AE",
};

let extraIndex: Map<string, string> | null = null;
let nameToCodes: Map<string, string[]> | null = null;

function fold(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function regionName(code: string): string | null {
  try {
    const name = displayNames.of(code);
    if (!name || name.toUpperCase() === code.toUpperCase()) return null;
    return name;
  } catch {
    return null;
  }
}

function aliases(): Map<string, string> {
  if (extraIndex) return extraIndex;
  const map = new Map<string, string>();
  for (const [alias, code] of Object.entries(EXTRA_ALIASES)) {
    map.set(fold(alias), code);
  }
  extraIndex = map;
  return map;
}

function names(): Map<string, string[]> {
  if (nameToCodes) return nameToCodes;
  const map = new Map<string, string[]>();
  for (let i = 65; i <= 90; i++) {
    for (let j = 65; j <= 90; j++) {
      const code = String.fromCharCode(i) + String.fromCharCode(j);
      const name = regionName(code);
      if (!name) continue;
      const key = fold(name);
      const list = map.get(key) ?? [];
      if (!list.includes(code)) list.push(code);
      map.set(key, list);
    }
  }
  nameToCodes = map;
  return map;
}

function preferCode(codes: string[]): string {
  const current = codes.filter((code) => !OBSOLETE_TO_CURRENT[code]);
  const pool = current.length ? current : codes;
  return [...pool].sort()[0];
}

/** Normalize site country values to ISO 3166-1 alpha-2 when possible. */
export function normalizeCountryToIso2(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const alias = aliases().get(fold(trimmed));
  if (alias) return alias;
  if (/^[a-zA-Z]{2}$/.test(trimmed)) {
    const code = trimmed.toUpperCase();
    if (OBSOLETE_TO_CURRENT[code]) return OBSOLETE_TO_CURRENT[code];
    return code;
  }
  const candidates = names().get(fold(trimmed));
  return candidates?.length ? preferCode(candidates) : null;
}

function countryFacetKey(value: string): string {
  return value.trim().toUpperCase();
}

/** Label for audience country facets — ISO when known, otherwise CRM site text (matches contact_search). */
export function countryFacetLabel(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return normalizeCountryToIso2(trimmed) ?? trimmed;
}

export function sortCountryLabels(countries: string[]): string[] {
  const byKey = new Map<string, string>();
  for (const raw of countries) {
    const label = raw.trim();
    if (!label) continue;
    const key = countryFacetKey(label);
    if (!byKey.has(key)) byKey.set(key, label);
  }
  return [...byKey.values()].sort((a, b) => {
    const aUs = countryFacetKey(a) === "US";
    const bUs = countryFacetKey(b) === "US";
    if (aUs !== bUs) return aUs ? -1 : 1;
    return a.localeCompare(b, "en", { sensitivity: "base" });
  });
}

export function distinctCountryFacetLabels(values: string[]): string[] {
  const byKey = new Map<string, string>();
  for (const raw of values) {
    const label = countryFacetLabel(String(raw));
    if (!label) continue;
    const key = countryFacetKey(label);
    if (!byKey.has(key)) byKey.set(key, label);
  }
  return sortCountryLabels([...byKey.values()]);
}

export function normalizeCountriesIn(values: string[]): string[] {
  return distinctCountryFacetLabels(values);
}

/**
 * contact_search matches country_code and site country with an exact uppercase compare.
 * A selected ISO code also has to include the name forms stored on intel.contacts.
 */
export function expandCountriesForSearch(countries: string[]): string[] {
  const out = new Set<string>();
  for (const raw of countries) {
    const label = countryFacetLabel(String(raw));
    if (!label) continue;
    out.add(label);
    if (!/^[A-Z]{2}$/.test(label)) continue;
    const name = regionName(label);
    if (name) out.add(name);
    for (const [alias, code] of Object.entries(EXTRA_ALIASES)) {
      if (code === label) out.add(alias);
    }
    for (const [obsolete, current] of Object.entries(OBSOLETE_TO_CURRENT)) {
      if (current === label) out.add(obsolete);
    }
  }
  return [...out];
}

export function hasUsCountry(countries: string[]): boolean {
  return countries.some((c) => {
    const iso = normalizeCountryToIso2(c);
    return iso === "US" || countryFacetKey(c) === "US" || countryFacetKey(c) === "UNITED STATES";
  });
}

export function rosterCountryLabels(row: Record<string, unknown>): string[] {
  const out: string[] = [];
  const site = String(row.site_country ?? "").trim();
  if (site) out.push(site);
  const code = String(row.country_code ?? "").trim();
  if (code) out.push(code);
  const country = String(row.country ?? "").trim();
  if (country) out.push(country);
  return out;
}
